import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [requiresTermsAcceptance, setRequiresTermsAcceptance] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          
          // Check if user has accepted terms
          try {
            const termsResponse = await api.get('/terms/check');
            setRequiresTermsAcceptance(!termsResponse.data.hasAccepted);
          } catch (error) {
            console.error('Error checking terms status:', error);
            // If we can't check terms, assume they need to accept
            setRequiresTermsAcceptance(true);
          }
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { user: userData, token: userToken, requiresTermsAcceptance: needsTerms } = response.data;
      
      setUser(userData);
      setToken(userToken);
      setRequiresTermsAcceptance(needsTerms || false);
      
      localStorage.setItem('token', userToken);
      localStorage.setItem('user', JSON.stringify(userData));
      api.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
      
      return { success: true, requiresTermsAcceptance: needsTerms || false };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { user: newUser, token: userToken, requiresTermsAcceptance: needsTerms } = response.data;
      
      setUser(newUser);
      setToken(userToken);
      setRequiresTermsAcceptance(needsTerms || false);
      
      localStorage.setItem('token', userToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
      
      return { success: true, requiresTermsAcceptance: needsTerms || false };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Registration failed' 
      };
    }
  };

  const googleLogin = async (credential) => {
    try {
      const response = await api.post('/auth/google/verify', { 
        credential
      });
      const { user: userData, token: userToken, isNewUser, requiresTermsAcceptance: needsTerms } = response.data;
      
      setUser(userData);
      setToken(userToken);
      setRequiresTermsAcceptance(needsTerms || false);
      
      localStorage.setItem('token', userToken);
      localStorage.setItem('user', JSON.stringify(userData));
      api.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
      
      return { success: true, isNewUser, requiresTermsAcceptance: needsTerms || false };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Google sign-in failed' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
  };

  const acceptTerms = async () => {
    try {
      // Get latest terms
      const termsResponse = await api.get('/terms/latest');
      const latestTerms = termsResponse.data;
      
      // Accept the terms
      await api.post('/terms/accept', { termsId: latestTerms.id });
      
      setRequiresTermsAcceptance(false);
      return { success: true };
    } catch (error) {
      console.error('Failed to accept terms:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to accept terms' 
      };
    }
  };

  const value = {
    user,
    token,
    login,
    register,
    googleLogin,
    logout,
    loading,
    requiresTermsAcceptance,
    acceptTerms
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};